import { useNavigate, useLocation, useNavigationType } from 'react-router-dom';
import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Enhanced navigation hook with browser history management
 * Provides better support for browser back/forward buttons
 */
export const useNavigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();
  const [canGoBack, setCanGoBack] = useState(false);
  const [canGoForward, setCanGoForward] = useState(false);
  const navigationStack = useRef([location.pathname]);
  const currentIndex = useRef(0);

  useEffect(() => {
    const currentPath = location.pathname;
    const stack = navigationStack.current;
    
    if (navigationType === 'PUSH') {
      navigationStack.current = [...stack.slice(0, currentIndex.current + 1), currentPath];
      currentIndex.current = navigationStack.current.length - 1;
    } else if (navigationType === 'POP') {
      const index = stack.indexOf(currentPath);
      if (index !== -1) {
        currentIndex.current = index;
      }
    }
    
    setCanGoBack(currentIndex.current > 0);
    setCanGoForward(currentIndex.current < navigationStack.current.length - 1);
  }, [location.pathname, navigationType]);

  const push = useCallback((to, state) => {
    const scrollPosition = window.scrollY;
    sessionStorage.setItem(`scroll-${location.pathname}`, scrollPosition.toString());
    
    navigate(to, { state });
  }, [navigate, location.pathname]);

  const replace = useCallback((to, state) => {
    navigate(to, { state, replace: true });
  }, [navigate]);

  const goBack = useCallback(() => {
    if (canGoBack) {
      navigate(-1);
    } else if (window.history.length > 1) {
      window.history.back();
    } else {
      navigate('/home');
    }
  }, [navigate, canGoBack]);

  const goForward = useCallback(() => {
    if (canGoForward) {
      navigate(1);
    }
  }, [navigate, canGoForward]);

  const go = useCallback((n) => {
    navigate(n);
  }, [navigate]);

  useEffect(() => {
    const savedPosition = sessionStorage.getItem(`scroll-${location.pathname}`);
    if (savedPosition) {
      window.scrollTo(0, parseInt(savedPosition, 10));
      sessionStorage.removeItem(`scroll-${location.pathname}`);
    }
  }, [location.pathname]);

  const getPreviousLocation = useCallback(() => {
    const stack = navigationStack.current;
    const idx = currentIndex.current;
    return idx > 0 ? stack[idx - 1] : null;
  }, []);

  const hasVisitedBefore = useCallback((path) => {
    return navigationStack.current.includes(path || location.pathname);
  }, [location.pathname]);

  const blockNavigation = useCallback((message) => {
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = message;
      return message;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  const history = {
    push,
    replace,
    go,
    goBack,
    goForward,
    location,
    action: navigationType,
    length: window.history.length,
    listen: (listener) => {
      const handlePopState = () => listener(location, navigationType);
      window.addEventListener('popstate', handlePopState);
      return () => window.removeEventListener('popstate', handlePopState);
    },
    block: blockNavigation,
    createHref: (to) => typeof to === 'string' ? to : to.pathname || '/',
    canGoBack,
    canGoForward,
    getPreviousLocation,
    hasVisitedBefore,
  };

  return {
    push,
    replace,
    goBack,
    goForward,
    go,
    navigate,
    
    location,
    pathname: location.pathname,
    search: location.search,
    hash: location.hash,
    state: location.state,
    
    canGoBack,
    canGoForward,
    navigationType,
    
    getPreviousLocation,
    hasVisitedBefore,
    blockNavigation,
    
    history,
  };
};

export const withNavigation = (Component) => {
  return (props) => {
    const navigation = useNavigation();
    return <Component {...props} {...navigation} />;
  };
};

export const usePersistentNavigation = (key = 'navigation-state') => {
  const navigation = useNavigation();
  
  useEffect(() => {
    const state = {
      pathname: navigation.location.pathname,
      search: navigation.location.search,
      state: navigation.location.state,
      timestamp: Date.now(),
    };
    sessionStorage.setItem(key, JSON.stringify(state));
  }, [navigation.location, key]);
  
  useEffect(() => {
    const savedState = sessionStorage.getItem(key);
    if (savedState) {
      try {
        const state = JSON.parse(savedState);
        if (Date.now() - state.timestamp < 3600000) {
          return state;
        }
      } catch (error) {
        console.error('Failed to restore navigation state:', error);
      }
    }
  }, [key]);
  
  return navigation;
};

export const useUnsavedChangesWarning = (hasUnsavedChanges = false, message = 'You have unsaved changes. Are you sure you want to leave?') => {
  const navigation = useNavigation();
  
  useEffect(() => {
    if (!hasUnsavedChanges) return;
    
    const unblock = navigation.blockNavigation(message);
    
    const handleClick = (e) => {
      const link = e.target.closest('a[href]');
      if (link && hasUnsavedChanges) {
        const shouldNavigate = window.confirm(message);
        if (!shouldNavigate) {
          e.preventDefault();
        }
      }
    };
    
    document.addEventListener('click', handleClick);
    
    return () => {
      unblock();
      document.removeEventListener('click', handleClick);
    };
  }, [hasUnsavedChanges, message, navigation]);
};

export default useNavigation;
